import { useEffect, useState } from "react";
import { CreditCard, Check, FileText, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";

export default function Billing() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/billing/subscription").then(({ data }) => setData(data));
    api.get("/billing/plans").then(({ data }) => setPlans(data));
  }, []);

  const checkout = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  if (!data) return <Loading />;
  const sub = data.subscription;
  const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

  return (
    <div>
      <PageHeader title="契約・請求" subtitle="法人単位の契約プランと請求管理" />

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4"><CreditCard className="w-5 h-5 text-blue-600" /><h3 className="font-semibold text-slate-800">現在の契約</h3></div>
        {sub ? (
          <div className="grid sm:grid-cols-4 gap-4">
            <div><div className="text-xs text-slate-400">プラン</div><div className="font-medium text-slate-800 mt-1">{sub.plan_name}</div></div>
            <div><div className="text-xs text-slate-400">契約状態</div><div className="mt-1"><StatusBadge status={sub.status} /></div></div>
            <div><div className="text-xs text-slate-400">利用開始日</div><div className="font-medium text-slate-800 mt-1">{sub.started_at || "-"}</div></div>
            <div><div className="text-xs text-slate-400">次回請求日</div><div className="font-medium text-slate-800 mt-1">{sub.next_billing || "-"}</div></div>
          </div>
        ) : <p className="text-sm text-slate-400">契約中のプランはありません。下記からお申し込みください。</p>}
      </div>

      <h3 className="font-semibold text-slate-800 mb-3">プラン</h3>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {plans.map((p) => {
          const active = sub?.plan === p.lookup_key;
          return (
            <div key={p.lookup_key} className={`bg-white rounded-xl border p-5 ${active ? "border-blue-400 ring-1 ring-blue-200" : "border-slate-200"}`} data-testid={`plan-${p.lookup_key}`}>
              <div className="font-semibold text-slate-800">{p.name}</div>
              <div className="mt-2 text-2xl font-bold text-slate-900">{yen(p.price)}<span className="text-sm text-slate-400 font-normal"> /{p.interval}</span></div>
              <ul className="mt-3 space-y-1 text-sm text-slate-500">
                <li className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> 全機能利用可能</li>
                <li className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> 法定研修・帳票・AI支援</li>
              </ul>
              <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700" disabled={busy === p.lookup_key || active} onClick={() => checkout(p.lookup_key)} data-testid={`checkout-${p.lookup_key}`}>
                {busy === p.lookup_key ? <Loader2 className="w-4 h-4 animate-spin" /> : active ? "契約中" : "このプランを申し込む"}
              </Button>
            </div>
          );
        })}
      </div>

      <h3 className="font-semibold text-slate-800 mb-3">請求履歴</h3>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {data.invoices?.length ? data.invoices.map((inv) => (
          <div key={inv.id} className="flex items-center justify-between p-4" data-testid={`invoice-${inv.id}`}>
            <div className="flex items-center gap-3"><FileText className="w-4 h-4 text-slate-400" /><div><div className="text-sm font-medium text-slate-800">{inv.invoice_no}</div><div className="text-xs text-slate-400">{inv.plan}・{(inv.created_at || "").slice(0, 10)}</div></div></div>
            <div className="flex items-center gap-3"><span className="font-medium text-slate-800">{yen(inv.amount)}</span><StatusBadge status={inv.status} /></div>
          </div>
        )) : <div className="p-6 text-sm text-slate-400 text-center">請求履歴はありません</div>}
      </div>

      <p className="text-xs text-slate-400 mt-4">※ クレジットカード情報はWCC内に保存されず、Stripe（外部決済サービス）で安全に処理されます。テストカード：4242 4242 4242 4242</p>
    </div>
  );
}
